import { useEffect, useState, useRef } from "react";

interface SSEProgress {
  batchId?: string;
  status?: string;
  progress?: number;
  message?: string;
  error?: string;
  completed?: boolean;
}

interface UseSSEOptions {
  maxRetries?: number;
  retryDelay?: number;
  onMessage?: (data: SSEProgress) => void;
  onError?: (error: Error) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
}

export function useSSE(url: string, options: UseSSEOptions = {}) {
  const {
    maxRetries = 5,
    retryDelay = 1000,
    onMessage,
    onError,
    onConnect,
    onDisconnect,
  } = options;

  const [progress, setProgress] = useState<SSEProgress>({});
  const [error, setError] = useState<Error | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  
  const eventSourceRef = useRef<EventSource | null>(null);
  const retryCountRef = useRef(0);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    let isMounted = true;

    const connect = () => {
      if (!isMounted || eventSourceRef.current) return;

      try {
        const token = localStorage.getItem("auth_token");
        const fullUrl = `${url}?token=${encodeURIComponent(token || "")}`;
        
        const eventSource = new EventSource(fullUrl);
        eventSourceRef.current = eventSource;

        eventSource.onopen = () => {
          if (!isMounted) return;
          
          setIsConnected(true);
          setError(null);
          retryCountRef.current = 0;
          
          // Clear any pending retry
          if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
          }
          
          onConnect?.();
          
          // Restore progress from localStorage if available
          const savedProgress = localStorage.getItem(`sse_progress_${url}`);
          if (savedProgress) {
            try {
              const parsed = JSON.parse(savedProgress);
              setProgress(parsed);
            } catch (e) {
              console.error("Failed to parse saved progress:", e);
            }
          }
        };

        eventSource.onmessage = (event) => {
          if (!isMounted) return;
          
          try {
            const data = JSON.parse(event.data);
            setProgress(data);
            
            // Save progress to localStorage for recovery
            localStorage.setItem(`sse_progress_${url}`, JSON.stringify(data));
            
            onMessage?.(data);
            
            // Clear saved progress if completed
            if (data.completed) {
              localStorage.removeItem(`sse_progress_${url}`);
            }
          } catch (e) {
            console.error("Failed to parse SSE message:", e);
          }
        };

        eventSource.onerror = (event) => {
          if (!isMounted) return;
          
          eventSource.close();
          eventSourceRef.current = null;
          setIsConnected(false);
          onDisconnect?.();
          
          // Attempt reconnection with exponential backoff
          if (retryCountRef.current < maxRetries) {
            const delay = retryDelay * Math.pow(2, retryCountRef.current);
            retryCountRef.current++;
            
            console.log(`SSE connection lost. Retrying in ${delay}ms... (attempt ${retryCountRef.current}/${maxRetries})`);
            
            retryTimeoutRef.current = setTimeout(() => {
              if (isMounted) {
                connect();
              }
            }, delay);
          } else {
            const error = new Error(`SSE connection failed after ${maxRetries} retries`);
            setError(error);
            onError?.(error);
          }
        };

        // Handle specific event types
        eventSource.addEventListener("error", (event: MessageEvent) => {
          if (!isMounted) return;
          
          const data = JSON.parse(event.data);
          const error = new Error(data.message || "SSE error");
          setError(error);
          onError?.(error);
        });

        eventSource.addEventListener("complete", (event: MessageEvent) => {
          if (!isMounted) return;
          
          const data = JSON.parse(event.data);
          setProgress({ ...data, completed: true });
          
          // Clean up after completion
          localStorage.removeItem(`sse_progress_${url}`);
          eventSource.close();
          eventSourceRef.current = null;
          setIsConnected(false);
        });

      } catch (err) {
        const error = err instanceof Error ? err : new Error("Failed to establish SSE connection");
        setError(error);
        onError?.(error);
      }
    };

    // Initial connection
    connect();

    // Cleanup
    return () => {
      isMounted = false;
      
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      
      setIsConnected(false);
    };
  }, [url, maxRetries, retryDelay, onMessage, onError, onConnect, onDisconnect]);

  // Manual reconnect function
  const reconnect = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    
    retryCountRef.current = 0;
    setError(null);
    
    // Trigger reconnection through effect
    setIsConnected(false);
  };

  return {
    progress,
    error,
    isConnected,
    reconnect,
  };
}
